import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { FileDown, Save, Calculator, TrendingUp, Table, Bookmark } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import InvestmentForm from '../components/calculadora/InvestmentForm';
import ResultsSummary from '../components/calculadora/ResultsSummary';
import ProjectionCharts from '../components/calculadora/ProjectionCharts';
import PaymentTable from '../components/calculadora/PaymentTable';
import SavedProjections from '../components/calculadora/SavedProjections';
import { generatePdf } from '../components/calculadora/PdfGenerator';
import BenchmarkComparison from '../components/calculadora/BenchmarkComparison';
import api from '../utils/api';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const initialFormData = {
  nome_cliente: '',
  empreendimento: '',
  valor_mercado: 1000000,
  valor_compra: 900000,
  valorizacao_anual: 10,
  correcao_anual: 4,
  prazo_meses: 120,
  data_inicio: getCurrentMonth(),
  conclusao_obra: '',
  entrada_percentual: 15,
  ato_valor: 0,
  sinal_valor: 0,
  parcela_mensal: 0,
  parcela_inicio_mes: 1,
  reforco_anual: 0,
  reforcos_custom: [],
  modo_reforco: 'baloes',
  divisao_ato_n: 1,
  taxa_juros_anual_financiamento: 11.60,
  proposta_aprovada: null,
  momento_saida: 'pos_chaves',
};

const tabs = [
  { id: 'dados', label: 'Dados', icon: Calculator },
  { id: 'projecao', label: 'Projeção', icon: TrendingUp },
  { id: 'cronograma', label: 'Cronograma', icon: Table },
  { id: 'salvas', label: 'Salvas', icon: Bookmark },
];

export default function CalculadoraFinanciamento({ onLogout }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tipoParam = searchParams.get('tipo') || '';
  // 'pre-lancamento' → 'pre', 'lancamento' → 'lancamento', sem param → 'lancamento', 'pronto' → 'pronto'
  const tipoSimulacao = tipoParam === 'pre-lancamento' ? 'pre' : tipoParam === 'pronto' ? 'pronto' : 'lancamento';

  const [formData, setFormData] = useState(initialFormData);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('dados');
  const [saving, setSaving] = useState(false);
  const [tese, setTese] = useState(null);
  const [teseLoading, setTeseLoading] = useState(false);
  const [benchmarks, setBenchmarks] = useState(null);
  const [benchLoading, setBenchLoading] = useState(false);

  const handleSaveProjection = useCallback(async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post('/calculadora/projecoes', {
        nome_cliente: formData.nome_cliente || '',
        empreendimento: formData.empreendimento || '',
        form_data: formData,
        result: result,
      });
      toast.success('Projeção salva com sucesso!');
    } catch (err) {
      toast.error('Erro ao salvar projeção');
    } finally {
      setSaving(false);
    }
  }, [result, formData]);

  const handleLoadProjection = useCallback((proj) => {
    const fd = { ...proj.form_data };
    // Migração: projeções antigas guardavam a divisão do ato como reforcos_custom com _divisao_ato
    const divAtoAntigos = (fd.reforcos_custom || []).filter(r => r._divisao_ato);
    if (divAtoAntigos.length > 0) {
      const n = divAtoAntigos.length + 1;
      const parcelaUnitaria = divAtoAntigos[0].valor || 0;
      const valorCompra = fd.valor_compra || 0;
      // Reconstitui o TOTAL da entrada
      if (fd.ato_valor && fd.ato_valor > 0) {
        fd.ato_valor = parcelaUnitaria * n;
      } else if (valorCompra > 0) {
        const totalEntrada = parcelaUnitaria * n;
        fd.entrada_percentual = Math.round((totalEntrada / valorCompra) * 10000) / 100;
      }
      fd.reforcos_custom = (fd.reforcos_custom || []).filter(r => !r._divisao_ato);
      fd.divisao_ato_n = n;
    } else if (!fd.divisao_ato_n) {
      fd.divisao_ato_n = 1;
    }
    setFormData(fd);
    setResult(proj.result);
    setActiveSection('projecao');
    toast.success(`Projeção de "${proj.nome_cliente || 'N/A'}" carregada`);
  }, []);

  const handleCalculate = useCallback(async () => {
    if (!formData.valor_mercado || formData.valor_mercado <= 0) {
      toast.error('Informe o valor de mercado do imóvel');
      return;
    }
    if (!formData.valor_compra || formData.valor_compra <= 0) {
      toast.error('Informe o valor de compra do imóvel');
      return;
    }

    setLoading(true);
    try {
      const taxaConstrucao = Number(formData.correcao_construcao_taxa) || 0;
      const taxaPosObra = (Number(formData.correcao_pos_taxa) || 0) + (Number(formData.correcao_pos_spread) || 0);
      const indiceConstrucao = formData.correcao_construcao_indice || 'CUB';
      const indicePos = formData.correcao_pos_indice || 'IPCA';
      const indicePosLabel = (Number(formData.correcao_pos_spread) || 0) > 0
        ? `${indicePos}+${formData.correcao_pos_spread}%`
        : indicePos;

      const payload = {
        valor_mercado: formData.valor_mercado,
        valor_compra: formData.valor_compra,
        valorizacao_anual: formData.valorizacao_anual,
        correcao_anual: taxaConstrucao || formData.correcao_anual || 0,  // mantém compat
        correcao_anual_construcao: taxaConstrucao,
        correcao_anual_pos_obra: taxaPosObra,
        indice_construcao: indiceConstrucao,
        indice_pos_obra: indicePosLabel,
        prazo_meses: formData.prazo_meses,
        data_inicio: formData.data_inicio,
        parcela_mensal: formData.parcela_mensal || 0,
        parcela_inicio_mes: formData.parcela_inicio_mes || 1,
        sinal_valor: formData.sinal_valor || 0,
      };

      if (formData.ato_valor > 0) {
        payload.ato_valor = formData.ato_valor;
        payload.entrada_percentual = 0;
      } else if (formData.entrada_percentual > 0) {
        payload.entrada_percentual = formData.entrada_percentual;
        payload.ato_valor = 0;
      }

      // Divisão da entrada em N parcelas (se o usuário escolheu Dividir entrada em Nx)
      const divisaoN = Math.max(1, parseInt(formData.divisao_ato_n || 1));
      let reforcosDivisaoAto = [];
      if (divisaoN > 1) {
        const totalEntrada = payload.ato_valor && payload.ato_valor > 0
          ? payload.ato_valor
          : (formData.valor_compra || 0) * (payload.entrada_percentual || 0) / 100;
        if (totalEntrada > 0) {
          const parcela = Math.round((totalEntrada / divisaoN) * 100) / 100;
          // 1ª parcela permanece como ato; demais viram reforços nos meses 2..N
          if (payload.ato_valor && payload.ato_valor > 0) {
            payload.ato_valor = parcela;
          } else {
            payload.entrada_percentual = (formData.valor_compra || 0) > 0
              ? Math.round((parcela / formData.valor_compra) * 10000) / 100
              : 0;
          }
          for (let i = 1; i < divisaoN; i++) {
            reforcosDivisaoAto.push({
              mes: i + 1,
              valor: parcela,
              tipo: 'divisao_ato',
              descricao: 'Parcela da Entrada',
            });
          }
        }
      }

      const modoReforco = formData.modo_reforco || 'baloes';
      const reforcosUser = (formData.reforcos_custom || []).filter(r => r.mes > 0 && r.valor > 0 && !r._divisao_ato);
      const reforcosList = [...reforcosUser, ...reforcosDivisaoAto];
      // Envia reforcos_custom sempre que houver entradas (custom, distribuir, ou divisão de entrada)
      if (reforcosList.length > 0 && modoReforco !== 'anual') {
        payload.reforcos_custom = reforcosList.map(r => ({
          mes: r.mes,
          valor: r.valor,
          ...(r.tipo ? { tipo: r.tipo } : {}),
          ...(r.descricao ? { descricao: r.descricao } : {}),
        }));
        payload.reforco_anual = 0;
        if (formData.conclusao_obra) payload.conclusao_obra = formData.conclusao_obra;
      } else if (modoReforco === 'anual' && formData.reforco_anual > 0) {
        payload.reforco_anual = formData.reforco_anual;
        if (formData.conclusao_obra) payload.conclusao_obra = formData.conclusao_obra;
      } else if (modoReforco === 'baloes' && formData.conclusao_obra) {
        payload.conclusao_obra = formData.conclusao_obra;
      }

      // Financiamento Bancário externo — só envia quando modo = bancario
      const tipoFin = formData.tipo_financiamento || 'direto';
      payload.tipo_financiamento = tipoFin;
      if (formData.indice_desconto) payload.indice_desconto = formData.indice_desconto;
      if (formData.taxa_desconto_anual) payload.taxa_desconto_anual = formData.taxa_desconto_anual;
      if (tipoFin === 'bancario') {
        if (formData.financiamento_pos_obra && formData.financiamento_pos_obra > 0) {
          payload.financiamento_bancario = Number(formData.financiamento_pos_obra);
          payload.financiamento_bancario_percentual = 0;
        } else if (formData.financiamento_pos_obra_percentual && formData.financiamento_pos_obra_percentual > 0) {
          payload.financiamento_bancario_percentual = Number(formData.financiamento_pos_obra_percentual);
          payload.financiamento_bancario = 0;
        }
      }

      // Parcela das Chaves (override do balão automático) - envia valor OU percentual
      const _pcv = Number(formData.parcela_chaves_valor) || 0;
      const _pcp = Number(formData.parcela_chaves_percentual) || 0;
      if (_pcv > 0) {
        payload.parcela_chaves_valor = _pcv;
      } else if (_pcp > 0) {
        payload.parcela_chaves_percentual = _pcp;
      }

      // Veículos no Negócio (cliente entrega veículos como parte do pagamento)
      if (Array.isArray(formData.veiculos) && formData.veiculos.length > 0) {
        payload.veiculos = formData.veiculos
          .filter(v => v && Number(v.valor) > 0)
          .map(v => ({
            valor: Number(v.valor),
            marca: v.marca != null ? String(v.marca) : null,
            modelo: v.modelo != null ? String(v.modelo) : null,
            ano: v.ano != null ? String(v.ano) : null,
            fipe_codigo: v.fipe_codigo != null ? String(v.fipe_codigo) : null,
          }));
      }

      const response = await api.post('/calculadora/calcular', payload);
      setResult(response.data);
      setActiveSection('projecao');
      toast.success('Cálculo realizado com sucesso!');
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(msg ? `Erro: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}` : 'Erro ao calcular investimento.');
    } finally {
      setLoading(false);
    }
  }, [formData]);

  React.useEffect(() => {
    if (!result) { setBenchmarks(null); return; }
    setBenchLoading(true);
    api.post('/calculadora/benchmark', { result, form_data: formData })
      .then(r => setBenchmarks(r.data))
      .catch(() => setBenchmarks(null))
      .finally(() => setBenchLoading(false));
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!result) { setTese(null); return; }
    setTeseLoading(true);
    api.post('/calculadora/tese', { result, form_data: formData })
      .then(r => setTese(r.data))
      .catch(() => setTese({ cenarios: [], perfil_produto: '', veredicto: 'Análise temporariamente indisponível.', alertas: [] }))
      .finally(() => setTeseLoading(false));
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carrega telefone do perfil salvo para o WhatsApp
  React.useEffect(() => {
    try {
      const perfil = JSON.parse(localStorage.getItem('tesiq_perfil') || '{}');
      if (perfil.telefone) {
        setFormData(prev => ({ ...prev, _perfil_telefone: perfil.telefone }));
      }
    } catch {}
  }, []); // eslint-disable-line

  const renderSection = () => {
    switch (activeSection) {
      case 'dados':
        return (
          <InvestmentForm
            formData={formData}
            setFormData={setFormData}
            onCalculate={handleCalculate}
            loading={loading}
            tipoSimulacao={tipoSimulacao}
          />
        );
      case 'projecao':
        return (
          <div className="space-y-8">
            <ResultsSummary result={result} formData={formData} />
            <ProjectionCharts result={result} />
            {/* Análise de Retorno e Custo de Oportunidade */}
            <BenchmarkComparison benchmarks={benchmarks} loading={benchLoading} result={result} formData={formData} onFormDataChange={setFormData} />

            {/* Adição 3 — Status da Proposta */}
            <div className="p-4 bg-white border border-stone-200 rounded-xl shadow-sm">
              <p className="text-sm font-semibold text-stone-700 mb-3">
                Proposta aprovada pela construtora?
              </p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'sim',     label: '✓ Sim, aprovada',  cls: 'bg-emerald-50 border-emerald-400 text-emerald-800' },
                  { value: 'nao',     label: '✗ Não aprovada',   cls: 'bg-red-50 border-red-400 text-red-800' },
                  { value: 'analise', label: '⏳ Em análise',     cls: 'bg-amber-50 border-amber-400 text-amber-800' },
                ].map(({ value, label, cls }) => (
                  <button
                    key={value}
                    onClick={() => setFormData(prev => ({ ...prev, proposta_aprovada: value }))}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                      formData.proposta_aprovada === value ? cls : 'bg-white border-stone-200 text-stone-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Adição 4 — Tese de Investimento TesIQ */}
            {teseLoading && (
              <div className="p-6 bg-stone-900 rounded-2xl text-white flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-stone-600 border-t-amber-400 rounded-full animate-spin" />
                <span className="text-stone-400 text-sm">Gerando tese de investimento...</span>
              </div>
            )}
            {tese && !teseLoading && (
              <div className="p-6 bg-stone-900 rounded-2xl text-white">
                <h3 className="font-heading text-xl font-bold text-amber-100 mb-1">
                  Tese de Investimento TesIQ
                </h3>
                <p className="text-xs text-stone-400 mb-5">
                  Análise independente · O corretor não pode alterar este resultado
                </p>
                {tese.cenarios && tese.cenarios.length > 0 && (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">
                      Cenários de Saída Projetados
                    </p>
                    {tese.cenarios.map(c => (
                      <div key={c.nome} className="flex justify-between items-center bg-white/5 rounded-lg px-4 py-3 mb-2">
                        <span className="text-sm text-stone-300">{c.nome}</span>
                        <span className="text-sm font-bold text-emerald-400">{c.resultado}</span>
                      </div>
                    ))}
                  </>
                )}
                {tese.perfil_produto && (
                  <p className="text-xs text-stone-400 mt-4 mb-4 leading-relaxed">{tese.perfil_produto}</p>
                )}
                {tese.alertas && tese.alertas.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {tese.alertas.map((a, i) => (
                      <p key={i} className="text-xs text-amber-300 bg-amber-900/30 rounded-md px-3 py-2">⚠ {a}</p>
                    ))}
                  </div>
                )}
                <div className={`rounded-xl p-4 text-center ${
                  tese.veredicto?.startsWith('RECOMENDADO') ? 'bg-emerald-800' :
                  tese.veredicto?.startsWith('NÃO') ? 'bg-red-900' : 'bg-stone-700'
                }`}>
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300 mb-1">
                    Veredicto TesIQ
                  </p>
                  <p className="text-base font-heading font-bold text-white">
                    {tese.veredicto}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="btn-salvar-projecao"
                onClick={handleSaveProjection}
                disabled={saving}
                className="flex-1 h-14 text-base flex items-center justify-center gap-3 rounded-lg font-medium bg-stone-100 hover:bg-stone-200 text-stone-800 transition-colors border border-stone-300"
              >
                {saving ? (
                  <div className="inline-block w-5 h-5 border-2 border-stone-300 border-t-stone-700 rounded-full animate-spin" />
                ) : (
                  <Save size={20} />
                )}
                {saving ? 'Salvando...' : 'Salvar Projeção'}
              </button>
              <button
                data-testid="btn-criar-pdf"
                onClick={() => generatePdf(result, formData, tese)}
                className="flex-1 h-14 text-base flex items-center justify-center gap-3 rounded-lg font-medium bg-primary hover:bg-primary/90 text-white transition-colors"
              >
                <FileDown size={22} />
                CRIAR PDF
              </button>
              <button
                data-testid="btn-whatsapp"
                onClick={async () => {
                  const nomeCliente = formData.nome_cliente || 'Cliente';
                  const empreendimento = formData.empreendimento || 'Simulação';
                  const valorImovel = result?.valor_compra
                    ? `R$ ${Number(result.valor_compra).toLocaleString('pt-BR')}`
                    : '';
                  const mensagem = encodeURIComponent(
                    `Olá ${nomeCliente}! 👋\n\nSegue a simulação do ${empreendimento}${valorImovel ? ` (${valorImovel})` : ''} que preparei para você.\n\nQualquer dúvida, estou à disposição!`
                  );
                  const numero = formData._perfil_telefone
                    ? formData._perfil_telefone.replace(/\D/g, '')
                    : '';
                  const url = numero
                    ? `https://wa.me/${numero}?text=${mensagem}`
                    : `https://wa.me/?text=${mensagem}`;
                  await generatePdf(result, formData, tese);
                  setTimeout(() => window.open(url, '_blank'), 800);
                }}
                className="h-14 px-5 flex items-center justify-center gap-2 rounded-lg font-semibold text-sm bg-[#25D366] hover:bg-[#20ba58] text-white transition-colors shrink-0"
                title="Baixar PDF e abrir WhatsApp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        );
      case 'cronograma':
        return <PaymentTable result={result} />;
      case 'salvas':
        return <SavedProjections onLoad={handleLoadProjection} />;
      default:
        return null;
    }
  };

  const sectionTitles = {
    dados: 'Simulador de Investimento',
    projecao: 'Projeção do Investimento',
    cronograma: 'Cronograma de Pagamentos',
    salvas: 'Simulações Salvas',
  };

  return (
    <div className="flex">
      <Sidebar onLogout={onLogout} />
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-7xl mx-auto space-y-6" data-testid="calculadora-financiamento">
      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`calc-tab-${tab.id}`}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeSection === tab.id
                ? 'bg-primary text-white shadow-md'
                : 'bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 border border-stone-200 dark:border-stone-600'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-heading font-bold text-stone-800 dark:text-white">
            {sectionTitles[activeSection]}
          </h2>
          {activeSection === 'dados' && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
              tipoSimulacao === 'pre' ? 'bg-amber-100 text-amber-800' :
              tipoSimulacao === 'pronto' ? 'bg-blue-100 text-blue-800' :
              'bg-emerald-100 text-emerald-800'
            }`}>
              {tipoSimulacao === 'pre' ? '🏗 Pré-lançamento' : tipoSimulacao === 'pronto' ? '🏠 Imóvel Pronto' : '🚀 Lançamento'}
            </span>
          )}
        </div>
        <p className="text-stone-500 dark:text-stone-400 mt-1 text-sm">
          {activeSection === 'dados' && 'Preencha os dados do imóvel para simular seu investimento'}
          {activeSection === 'projecao' && 'Visualize a projeção completa do seu investimento'}
          {activeSection === 'cronograma' && 'Acompanhe o cronograma detalhado de pagamentos'}
          {activeSection === 'salvas' && 'Gerencie suas simulações salvas'}
        </p>
      </div>

      {/* Quick Actions */}
      {activeSection !== 'dados' && result && (
        <div className="p-4 bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-stone-600 dark:text-stone-400">Simulação atual</p>
              <p className="font-semibold text-stone-800 dark:text-white">
                {formData.prazo_meses} meses | {formData.valorizacao_anual}% val. | {formData.correcao_anual}% corr.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[36, 60, 100, 120, 140, 180].map((prazo) => (
                <button
                  key={prazo}
                  data-testid={`quick-prazo-${prazo}`}
                  onClick={() => {
                    setFormData({ ...formData, prazo_meses: prazo });
                    setTimeout(() => handleCalculate(), 100);
                  }}
                  className={`prazo-btn ${formData.prazo_meses === prazo ? 'active' : ''}`}
                >
                  {prazo}m
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {renderSection()}
      </div>

      {/* Empty State */}
      {(activeSection === 'projecao' || activeSection === 'cronograma') && !result && (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 rounded-full mb-4">
            <Calculator className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="text-lg font-semibold text-stone-700 dark:text-stone-300 mb-2">
            Nenhuma simulação realizada
          </h3>
          <p className="text-stone-500 mb-6">
            Preencha os dados do investimento para ver a projeção
          </p>
          <button
            data-testid="btn-go-to-dados"
            onClick={() => setActiveSection('dados')}
            className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors"
          >
            Ir para Dados do Investimento
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
